import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { RAILS_API_URL } from 'config/apiUrl'
import { RootState } from 'redux/store'
import nestApiService from 'services/api/nest-api-service'
import railsApiService from 'services/api/rails-api-service'
import { Profile } from 'types'

export interface AuthState {
  isLoading: boolean
  isLoggedIn: boolean
  profile: Profile | null
  railsToken: string | null
  railsTokenType: string | null
  railsRefreshToken: string | null
  nestToken: string | null
}

const initialState: AuthState = {
  isLoading: false,
  isLoggedIn: false,
  profile: null,
  railsToken: null,
  railsTokenType: null,
  railsRefreshToken: null,
  nestToken: null
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signOut: (state) => {
      // Disconnect Action Cable before signing out
      if (typeof window !== 'undefined') {
        import('utils/actioncableHelper').then(({ disconnectActionCable }) => {
          disconnectActionCable()
        })
      }
      state = initialState
      return state
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.isLoading = false
        state.isLoggedIn = true
        state.profile = action.payload
      })
      .addCase(getProfile.rejected, (state) => {
        state.isLoading = false
        state.isLoggedIn = false
        state.profile = null
      })
      .addCase(createRailsSession.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createRailsSession.fulfilled, (state, action) => {
        const { accessToken, tokenType, refreshToken } = action.payload

        state.railsToken = accessToken
        state.railsTokenType = tokenType
        state.railsRefreshToken = refreshToken

        state.isLoading = false
        state.isLoggedIn = true
      })
      .addCase(createRailsSession.rejected, (state) => {
        state.isLoading = false
        state.isLoggedIn = false
        state.railsToken = null
        state.railsTokenType = null
        state.railsRefreshToken = null
      })
      .addCase(registerAfterPayment.fulfilled, (state, action) => {
        const { accessToken, tokenType, refreshToken } = action.payload
        state.railsToken = accessToken
        state.railsTokenType = tokenType
        state.railsRefreshToken = refreshToken
        state.isLoading = false
        state.isLoggedIn = true
      })
      .addCase(registerAfterPayment.pending, (state) => {
        state.isLoading = true
      })
      .addCase(registerAfterPayment.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(refreshRailsSession.fulfilled, (state, action) => {
        const { accessToken, tokenType, refreshToken } = action.payload

        state.railsToken = accessToken
        state.railsTokenType = tokenType
        state.railsRefreshToken = refreshToken

        state.isLoading = false
        state.isLoggedIn = true
      })
      .addCase(createNestToken.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createNestToken.fulfilled, (state, action) => {
        const { token } = action.payload

        state.nestToken = token

        state.isLoading = false
        state.isLoggedIn = true
      })
      .addCase(createNestToken.rejected, (state) => {
        state.isLoading = false
        state.isLoggedIn = false
        state.nestToken = null
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false
        state.profile = action.payload
      })
      .addCase(updateProfile.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(confirmLillyRegistration.fulfilled, (state, action) => {
        state.profile = action.payload
      })
})

export const { signOut } = authSlice.actions

export default authSlice.reducer

interface RailsTokenResponse {
  accessToken: string
  tokenType: string
  expiresIn: number
  refreshToken: string
  createdAt: number
}

export const getProfile = createAsyncThunk('get-profile', async (params, { rejectWithValue }) => {
  try {
    const { data } = await railsApiService.apiInstance.get<Profile>('/api/v1/profile')

    return data
  } catch (error) {
    return rejectWithValue(error)
  }
})

interface CreateRailsTwoFactorTokenParams {
  username: string
  password: string
  cookies_enabled?: boolean
}

export const createRailsTwoFactorToken = createAsyncThunk(
  'create-rails-two-factor-token',
  async ({ username, password, cookies_enabled = true }: CreateRailsTwoFactorTokenParams, { rejectWithValue }) => {
    try {
      const { data } = await railsApiService.apiInstance.post('/spree_oauth/two_factor_token', {
        username,
        password,
        cookies_enabled
      })

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

interface CreateRailsSessionParams {
  username: string
  password: string
  token?: string
}

export const createRailsSession = createAsyncThunk(
  'create-rails-session',
  async (params: CreateRailsSessionParams, { rejectWithValue }) => {
    try {
      const { data } = await railsApiService.apiInstance.post<RailsTokenResponse>('/spree_oauth/token', {
        grant_type: 'password',
        token: '',
        ...params
      })

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

interface RegisterAfterPaymentParams {
  email: string
  password: string
  passwordConfirmation: string
  consultationId: number
  patientInfoToken?: string
}

interface RegisterAfterPaymentResponse extends RailsTokenResponse {
  user: {
    id: number
    email: string
    emailConfirmedAt: string | null
    emailConfirmationRequiredAfter: string | null
  }
  consultation: { id: number; state: string }
}

export const registerAfterPayment = createAsyncThunk<
  RegisterAfterPaymentResponse,
  RegisterAfterPaymentParams,
  { rejectValue: { code?: string; status?: number; email?: string } }
>('register-after-payment', async (params, { rejectWithValue }) => {
  try {
    const { data } = await railsApiService.apiInstance.post<RegisterAfterPaymentResponse>(
      '/api/v1/registrations/post_payment',
      params,
      { headers: { Authorization: '' } }
    )
    return data
  } catch (error) {
    const status = (error as { status?: number })?.status
    const data = (error as { data?: { code?: string; email?: string } })?.data
    return rejectWithValue({ status, code: data?.code, email: data?.email })
  }
})

interface RefreshRailsSessionParams {
  refresh_token: string
}

export const refreshRailsSession = createAsyncThunk(
  'refresh-rails-session',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { railsRefreshToken } = (getState() as RootState).auth

      const { data } = (await railsApiService.apiInstance.post<RailsTokenResponse>('/spree_oauth/token', {
        grant_type: 'refresh_token',
        refresh_token: railsRefreshToken
      })) as { data: RailsTokenResponse }

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

interface CreateNestTokenParams {
  accessToken: string
}

export const createNestToken = createAsyncThunk(
  'create-nest-token',
  async ({ accessToken }: CreateNestTokenParams, { rejectWithValue }) => {
    try {
      const { data } = await nestApiService.apiInstance.post<{
        token: string
      }>(
        '/auth',
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      )

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const destroySession = createAsyncThunk('destroy-session', async (_, { rejectWithValue, getState }) => {
  try {
    const { railsToken } = (getState() as RootState).auth

    return await railsApiService.apiInstance.post('/spree_oauth/revoke', {
      token: railsToken
    })
  } catch (error) {
    return rejectWithValue(error)
  }
})

interface UpdateProfileParams {
  first_name: string
  last_name: string
  birth_date: string
  prescription_sex: 'M' | 'F'
  email: string
  rg_number: string
  phone_number: string
  zip_code: string
  street_name: string
  street_number: string
  address_complement: string
  neighborhood: string
  city: string
  state: string
}

export const updateProfile = createAsyncThunk(
  'update-profile',
  async (params: Partial<UpdateProfileParams>, { rejectWithValue }) => {
    try {
      const { data } = await railsApiService.apiInstance.patch<Profile>('/api/v1/profile', {
        profile: params
      })

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const confirmLillyRegistration = createAsyncThunk(
  'confirm-lilly-registration',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await railsApiService.apiInstance.patch<Profile>('/api/v1/profile/lilly_registration')
      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)
